import React, { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, UserPlus, X } from 'lucide-react';
import { EinweisungTeilnehmer } from '../sige-plan/types';
import { SignaturePad } from '../common/SignaturePad';
import {
  ErfassungsSchritt,
  beschreibeErfassungsStand,
  darfZurUnterschrift,
  erfasseTeilnehmer,
  naechsteErfassung,
} from './einweisungLogic';

interface Props {
  teilnehmer: EinweisungTeilnehmer[];
  onChange: (liste: EinweisungTeilnehmer[]) => void;
  onBeenden: () => void;
}

/**
 * TEILNEHMER REIHUM ERFASSEN — der „Gerät herumreichen"-Modus.
 *
 * Auf der Baustelle steht die Kolonne beieinander und das Telefon wandert von
 * Hand zu Hand. Wer es bekommt, soll GENAU EINE Sache vor sich haben:
 * Name und Firma → unterschreiben → weitergeben. Deshalb liegt diese Strecke
 * ganzflaechig ueber dem Editor und zeigt weder die Zeilen der anderen noch
 * die uebrigen Knoepfe des Blattes — ein fremder Daumen soll das Blatt weder
 * abschliessen noch eine fremde Unterschrift loeschen koennen.
 *
 * ─── Warum jeder SOFORT in die Liste geht ─────────────────────────────────
 * Der Teilnehmer wird nicht am Ende des Laufs uebergeben, sondern in dem
 * Moment, in dem er unterschrieben hat. Faellt das Telefon aus der Hand oder
 * raeumt das Betriebssystem den Browser weg (Kamera!), stehen die bereits
 * geleisteten Unterschriften trotzdem im Blatt und laufen in Geraetesicherung
 * und automatisches Speichern. Sonst verloere ein Absturz beim elften
 * Teilnehmer die zehn davor — und die bekommt man nicht zurueck, wenn die
 * Leute wieder auf dem Geruest stehen.
 */
export const TeilnehmerErfassung: React.FC<Props> = ({ teilnehmer, onChange, onBeenden }) => {
  const [schritt, setSchritt] = useState<ErfassungsSchritt>('daten');
  const [name, setName] = useState('');
  const [firma, setFirma] = useState('');
  /** Funktion auf der Baustelle (Polier, Bauleiter …) — optional, bleibt wie die Firma stehen. */
  const [funktion, setFunktion] = useState('');
  /** Name des zuletzt Erfassten — nur fuer die Bestaetigungsseite. */
  const [zuletzt, setZuletzt] = useState<string>('');
  const nameRef = useRef<HTMLInputElement | null>(null);

  /**
   * Uebernimmt den aktuellen Teilnehmer und geht auf die Bestaetigungsseite.
   * `unterschrift` fehlt, wenn jemand nicht unterschreiben kann — er steht
   * dann trotzdem auf dem Blatt und unterschreibt auf dem Ausdruck.
   */
  const uebernehmen = (unterschrift?: string) => {
    const person = name.trim();
    if (!darfZurUnterschrift(person)) return;
    const id = crypto.randomUUID();
    const rolle = funktion.trim();
    const liste = erfasseTeilnehmer(teilnehmer, {
      id,
      name: person,
      firma,
      unterschrift,
      jetztIso: new Date().toISOString(),
    });
    // Die Funktion ist ein Zusatz am fertigen Eintrag — erfasseTeilnehmer
    // kennt nur Name, Firma und Unterschrift.
    onChange(rolle ? liste.map((t) => (t.id === id ? { ...t, funktion: rolle } : t)) : liste);
    setZuletzt(person);
    setSchritt('bestaetigt');
  };

  const naechster = () => {
    const vor = naechsteErfassung(firma);
    setName(vor.name);
    setFirma(vor.firma);
    setSchritt('daten');
    // Der Naechste soll lostippen koennen, ohne erst ins Feld zu treffen.
    window.setTimeout(() => nameRef.current?.focus(), 50);
  };

  // ─── Schritt 2: unterschreiben ───────────────────────────────────────────
  if (schritt === 'unterschrift') {
    return (
      <SignaturePad
        titel={`Unterschrift — ${name.trim()}`}
        hinweis="Bitte im Feld unterschreiben und übernehmen."
        alsOverlay
        abbrechenLabel="Zurück"
        onUebernehmen={(dataUrl) => uebernehmen(dataUrl)}
        onAbbrechen={() => setSchritt('daten')}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-100 flex flex-col">
      {/* Kopfzeile: Zaehlstand und Ausstieg. Der Ausstieg ist bewusst klein —
          weitergereicht wird das Geraet oft mit dem Daumen auf dem Rand. */}
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-slate-200">
        <span className="text-sm font-bold text-slate-800">Teilnehmer erfassen</span>
        <span className="flex-1 text-xs text-slate-500 truncate">{beschreibeErfassungsStand(teilnehmer)}</span>
        <button
          type="button"
          onClick={onBeenden}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-xl"
          title="Erfassung beenden"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex items-start justify-center">
        <div className="w-full max-w-md space-y-4 pt-2">

          {/* ─── Schritt 1: Name und Firma ─────────────────────────────── */}
          {schritt === 'daten' && (
            <>
              <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
                <label className="block space-y-1.5">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Name</span>
                  <input
                    ref={nameRef}
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && darfZurUnterschrift(name)) setSchritt('unterschrift');
                    }}
                    placeholder="Vor- und Nachname"
                    className="w-full px-4 py-3 min-h-[52px] text-base text-slate-900 bg-slate-50 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-300"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Firma</span>
                  <input
                    value={firma}
                    onChange={(e) => setFirma(e.target.value)}
                    placeholder="Betrieb (optional)"
                    className="w-full px-4 py-3 min-h-[52px] text-base text-slate-900 bg-slate-50 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-300"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Funktion</span>
                  <input
                    value={funktion}
                    onChange={(e) => setFunktion(e.target.value)}
                    maxLength={80}
                    placeholder="z. B. Polier, Bauleiter, Monteur (optional)"
                    className="w-full px-4 py-3 min-h-[52px] text-base text-slate-900 bg-slate-50 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-300"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={() => setSchritt('unterschrift')}
                disabled={!darfZurUnterschrift(name)}
                className="w-full min-h-[56px] flex items-center justify-center gap-2 text-base font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition-colors disabled:opacity-40"
              >
                Weiter zur Unterschrift <ArrowRight className="w-5 h-5" />
              </button>

              {/* Der Baustellenfall: Handschuh, verletzte Hand, „unterschreibt
                  spaeter auf dem Ausdruck". Er kommt trotzdem aufs Blatt. */}
              <button
                type="button"
                onClick={() => uebernehmen()}
                disabled={!darfZurUnterschrift(name)}
                className="w-full min-h-[44px] text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-40"
              >
                Ohne Unterschrift übernehmen
              </button>
            </>
          )}

          {/* ─── Schritt 3: weiterreichen oder beenden ─────────────────── */}
          {schritt === 'bestaetigt' && (
            <>
              <div className="bg-white rounded-2xl border border-emerald-200 p-5 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-base font-bold text-slate-800 truncate">{zuletzt} ist erfasst</p>
                  <p className="text-xs text-slate-500 mt-0.5">{beschreibeErfassungsStand(teilnehmer)}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={naechster}
                className="w-full min-h-[56px] flex items-center justify-center gap-2 text-base font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-2xl transition-colors"
              >
                <UserPlus className="w-5 h-5" /> Nächster Teilnehmer
              </button>
              <button
                type="button"
                onClick={onBeenden}
                className="w-full min-h-[52px] flex items-center justify-center gap-2 text-base font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-2xl transition-colors"
              >
                <Check className="w-5 h-5" /> Beenden
              </button>
              {/* Rueckweg in den Editor, ohne den Lauf zu beenden — hilft dem
                  Koordinator, wenn er sich beim Namen vertippt hat. */}
              <button
                type="button"
                onClick={() => setSchritt('daten')}
                className="w-full min-h-[44px] flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-slate-700"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück zur Eingabe
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
